// Design System Autocomplete Component
// Unified autocomplete/combobox component

import React, { forwardRef } from 'react';
import { 
  Autocomplete as MuiAutocomplete,
  TextField,
  Chip,
  styled,
  alpha
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens, transitionTokens } from '../../tokens';

// Styled Autocomplete with design system tokens
const StyledAutocomplete = styled(MuiAutocomplete)(({ theme }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    marginBottom: spacingTokens.component.md,
    
    '& .MuiAutocomplete-inputRoot': {
      borderRadius: borderTokens.lg,
      backgroundColor: alpha(colors.background.paper, mode === 'dark' ? 0.5 : 0.95),
      
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(colors.divider, mode === 'dark' ? 0.4 : 0.5),
        borderWidth: 1,
        transition: transitionTokens.fast,
      },
      
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary.main,
      },
      
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary.main,
        borderWidth: 2,
      },
      
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: `${colors.error.main} !important`,
        borderWidth: 2,
      },
    },
    
    '& .MuiAutocomplete-tag': {
      backgroundColor: alpha(colors.primary.main, 0.1),
      color: colors.primary.main,
      
      '& .MuiChip-deleteIcon': {
        color: alpha(colors.primary.main, 0.7),
        '&:hover': {
          color: colors.primary.main,
        },
      },
    },
    
    '& .MuiAutocomplete-listbox': {
      '& .MuiAutocomplete-option': {
        '&[aria-selected="true"]': {
          backgroundColor: alpha(colors.primary.main, 0.1),
        },
        '&.Mui-focused': {
          backgroundColor: alpha(colors.primary.main, 0.05),
        },
      },
    },
  };
});

/**
 * Autocomplete - Unified autocomplete/combobox component
 * 
 * Features:
 * - Design system tokens integration
 * - Single and multiple selection
 * - Customizable option rendering
 * - Loading state
 * - Grouped options support
 * - Free solo mode for custom values
 * 
 * @example
 * // Basic usage
 * <Autocomplete
 *   label="Country"
 *   options={countries}
 *   value={country}
 *   onChange={setCountry}
 * />
 * 
 * // Multiple selection with chips
 * <Autocomplete
 *   label="Tags"
 *   options={tags}
 *   value={selectedTags}
 *   onChange={setSelectedTags}
 *   multiple
 * />
 * 
 * // With custom option labels
 * <Autocomplete
 *   label="User"
 *   options={users}
 *   getOptionLabel={(option) => option.name}
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 * />
 */
const Autocomplete = forwardRef(({
  label,
  error,
  helperText,
  fullWidth = true,
  size = 'medium',
  loading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  placeholder,
  ...props
}, ref) => {
  return (
    <StyledAutocomplete
      ref={ref}
      fullWidth={fullWidth}
      size={size}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={helperText}
          placeholder={placeholder}
          variant="outlined"
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={index}
            label={props.getOptionLabel ? props.getOptionLabel(option) : option}
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
      {...props}
    />
  );
});

Autocomplete.propTypes = {
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
  noOptionsText: PropTypes.node,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  // All other MUI Autocomplete props are supported
};

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;